// Successor-product popup — e.g. AE68 Pro PDP promotes the new AE68 Max.
// Shows once per browser session, ~3s after the PDP mounts.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { productByHandle } from '../../lib/shopify/queries';

const UPGRADES = {
        'everglide-ae68-pro-he-keyboard': {
                target: 'everglide-ae68-max-he',
                eyebrow: 'New & improved',
                headline: 'Meet the AE68 Max',
                copy: 'The successor to the AE68 Pro has landed. See what’s new before you lock in your board.',
                cta: 'View AE68 Max',
        },
        'scyrox-v6': {
                target: 'scyrox-v6s',
                eyebrow: 'New & improved',
                headline: 'Meet the Scyrox V6S',
                copy: 'The successor to the Scyrox V6 has landed. See what’s new before you lock in your mouse.',
                cta: 'View Scyrox V6S',
        },
        'scyrox-v8': {
                target: 'scyrox-v8s',
                eyebrow: 'New & improved',
                headline: 'Meet the Scyrox V8S',
                copy: 'The successor to the Scyrox V8 has landed. See what’s new before you lock in your mouse.',
                cta: 'View Scyrox V8S',
        },
        'madlions-mad60-he-rgb-series-mad60he': {
                target: 'madlions-mad-60-68-v2-series',
                eyebrow: 'New & improved',
                headline: 'Meet the MAD 60/68 V2',
                copy: 'The successor to the MAD60 HE has landed. See what’s new before you lock in your board.',
                cta: 'View MAD 60/68 V2',
        },
};

const SESSION_KEY = (h) => `loadout.upgrade-popup.seen.${h}`;

export default function UpgradePopup({ handle }) {
        const cfg = UPGRADES[handle];
        const [open, setOpen] = useState(false);
        const [target, setTarget] = useState(null);

        useEffect(() => {
                if (!cfg) return undefined;
                try { if (window.sessionStorage.getItem(SESSION_KEY(handle))) return undefined; } catch { /* ignore */ }
                let alive = true;
                // Open at max(3s, target fetch) so the card never pops in half-empty.
                const delay = new Promise((r) => setTimeout(r, 3000));
                const fetchTarget = productByHandle(cfg.target).then((p) => { if (alive) setTarget(p); }).catch(() => {});
                Promise.all([delay, fetchTarget]).then(() => {
                        if (!alive) return;
                        setOpen(true);
                        try { window.sessionStorage.setItem(SESSION_KEY(handle), '1'); } catch { /* ignore */ }
                });
                return () => { alive = false; };
        }, [handle, cfg]);

        useEffect(() => {
                if (!open) return undefined;
                const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
                document.addEventListener('keydown', onKey);
                return () => document.removeEventListener('keydown', onKey);
        }, [open]);

        if (!cfg || !open) return null;
        const img = target?.featuredImage?.url || target?.images?.[0]?.url;
        const price = Number(target?.priceRange?.minVariantPrice?.amount);
        return (
                <div data-testid="upgrade-popup" className="fixed inset-0 z-[80] flex items-center justify-center bg-black/75 backdrop-blur-sm px-4" onClick={() => setOpen(false)}>
                        <div className="relative w-full max-w-[520px] border border-[#FF9F1C]/60 bg-[#0A0A0A] animate-in fade-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
                                <button type="button" onClick={() => setOpen(false)} aria-label="Close" data-testid="upgrade-popup-close" className="absolute top-3 right-3 h-9 w-9 flex items-center justify-center text-white/60 hover:text-white transition-colors">
                                        <X className="h-5 w-5" />
                                </button>
                                {img && (
                                        <div className="bg-[#111] h-[240px] flex items-center justify-center border-b border-white/10 p-4">
                                                <img src={img} alt={target?.title || cfg.headline} className="w-full h-full object-contain" />
                                        </div>
                                )}
                                <div className="p-6">
                                        <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#FF9F1C]">{cfg.eyebrow}</p>
                                        <h2 className="mt-2 font-display text-[28px] uppercase leading-none">{cfg.headline}</h2>
                                        <p className="mt-3 font-sans text-[14px] text-white/70 leading-relaxed">{cfg.copy}</p>
                                        {Number.isFinite(price) && price > 0 && (
                                                <p className="mt-3 font-mono text-[18px] tabular-nums">₹{price.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                                        )}
                                        <div className="mt-6 flex gap-3">
                                                <Link to={`/p/${cfg.target}`} onClick={() => setOpen(false)} data-testid="upgrade-popup-cta" className="flex-1 text-center border border-[#FF9F1C] bg-[#FF9F1C] text-[#0A0A0A] font-mono text-[12px] tracking-[0.2em] uppercase py-3.5 hover:bg-transparent hover:text-[#FF9F1C] transition-colors">
                                                        {cfg.cta} →
                                                </Link>
                                                <button type="button" onClick={() => setOpen(false)} data-testid="upgrade-popup-dismiss" className="px-5 border border-white/25 font-mono text-[11px] tracking-[0.2em] uppercase hover:border-white transition-colors">
                                                        Not now
                                                </button>
                                        </div>
                                </div>
                        </div>
                </div>
        );
}
